import * as React from 'react'

import { Box, Button, type Icon, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import moment from 'moment'

import { observer } from 'mobx-react'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import type { ActivatePayload } from 'modules/app_store/lib/SubscriptionAddonInstaller'
import PolicyForm from 'modules/payroll_bundle/pages/settings/Components/PayrollPolicy/Form/PolicyForm'
import type { Policies } from 'modules/payroll_bundle/store/collections/policies'
import Policy, { MONTHLY } from 'modules/payroll_bundle/store/models/Policy'

import FormBody from './FormBody'
import FormButton from './FormButton'
import { SHOW_BOOKKEEPER, SHOW_COUNTRY, SHOW_PAYROLL_POLICY } from './constants'
import useSetStage from './hooks/useSetStage'

interface Props {
  onSuccess: (payload: ActivatePayload) => void
  onClose: () => void
  policies: Policies
  loadingPolicies: boolean
}

interface SubSettings {
  title: string
  description: string | React.ReactNode
  illustration: React.ComponentProps<typeof Icon.Large>['icon']
}
interface Settings {
  [key: string]: SubSettings
}

const BookkeeperSettingsDescription = () => {
  return (
    <>
      <Text.Normal>
        {i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.modal_description')}
      </Text.Normal>
      <Box paddingTop="s8">
        <HelpCenterLink name="access_to_bookkeeper">
          {i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.access_to_bookkeeper')}
        </HelpCenterLink>
      </Box>
    </>
  )
}

const PayrollAppModal = ({ onSuccess, onClose, policies, loadingPolicies }: Props) => {
  const [submittingPolicy, setSubmittingPolicy] = React.useState(false)
  const [addEmployeesToPolicy, setAddEmployeesToPolicy] = React.useState<boolean>(true)
  const [bookkeeperIds, setBookkeeperIds] = React.useState<number[]>([])

  const countriesForm = React.useRef(
    new Form(
      {
        country: null,
      },
      {
        country: 'string',
      }
    )
  ).current

  const { company } = useCurrentSession()
  const policy = React.useMemo(
    () => {
      if (loadingPolicies) return null

      if (policies.isEmpty) {
        return new Policy({
          name: '',
          starts_on: moment().set('date', 1).format('YYYY-MM-DD'),
          periodicity: MONTHLY,
          recurrence: Policy.periodicities().monthly.recurrence,
          default: true, // Set first policy as default
          company_id: company.id,
        })
      } else {
        const defaultPolicy = policies.filter((policy) => policy.attributes.default)[0]
        defaultPolicy?.set({
          periodicity: Policy.periodicityKeyByRecurrence(defaultPolicy.attributes.recurrence),
        })
        return defaultPolicy
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [submittingPolicy]
  )

  const { secondStage, stage, setStage } = useSetStage({ policy })

  const policyForm = useLazyRef(
    () =>
      new PolicyForm({
        name: policy?.attributes.name,
        periodicity: policy?.attributes.periodicity,
        starts_on: policy?.attributes.starts_on,
        recurrence: policy?.attributes.recurrence,
        default: policy?.attributes.default,
        company_id: policy?.attributes.company_id,
      })
  ).current

  const onSubmit = () => {
    onSuccess({
      bookkeeper_id: bookkeeperIds[0], // To be removed once the migration is completed
      bookkeeper_ids: bookkeeperIds,
      country: countriesForm.get('country').value,
      payroll_policy_id: policy?.attributes.id,
      add_employees_to_policy: addEmployeesToPolicy,
    })
    onClose()
  }

  const onSubmitStage = () => {
    if (stage === SHOW_COUNTRY) {
      setStage(secondStage)
    } else if (stage === SHOW_PAYROLL_POLICY) {
      onSubmitPayrollPolicy()
    }
  }

  const onSubmitPayrollPolicy = async () => {
    if (!policy) return

    setSubmittingPolicy(true)

    const { success } = await asyncAction(() => policyForm.save(policy))

    if (success) {
      await policies.fetch()
      setStage(SHOW_BOOKKEEPER)
    }

    setSubmittingPolicy(false)
  }

  const canCreatePolicy =
    !submittingPolicy &&
    !!policyForm.get('starts_on').value &&
    !!policyForm.get('recurrence').value &&
    !!policyForm.get('name').value

  const countrySettings: SubSettings = {
    illustration: 'location-pin',
    title: i18n.t('app_store_v2.apps.payroll_bundle.install_modal.country.title'),
    description: i18n.t('app_store_v2.apps.payroll_bundle.install_modal.country.description'),
  }

  const bookeeperSettings: SubSettings = {
    illustration: 'user-tie',
    title: i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.title'),
    description: <BookkeeperSettingsDescription />,
  }

  const policySettings: SubSettings = {
    illustration: 'money-bag',
    title: policy?.attributes.id
      ? i18n.t('app_store_v2.apps.payroll_bundle.install_modal.payroll_policy.update_title')
      : i18n.t('app_store_v2.apps.payroll_bundle.install_modal.payroll_policy.title'),
    description: i18n.t(
      'app_store_v2.apps.payroll_bundle.install_modal.payroll_policy.description'
    ),
  }

  const settings: Settings = {
    SHOW_BOOKKEEPER: bookeeperSettings,
    SHOW_PAYROLL_POLICY: policySettings,
    SHOW_COUNTRY: countrySettings,
  }

  const { title, description, illustration } = settings[stage] ?? {}

  return (
    <Modal
      illustration={illustration}
      title={title ?? ''}
      description={description}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <FormButton
            stage={stage}
            policy={policy}
            bookkeeperIds={bookkeeperIds}
            onSubmitStage={onSubmitStage}
            canCreatePolicy={canCreatePolicy}
            disableCountryButton={!countriesForm.get('country').value?.length}
          />
        </>
      }
    >
      <FormBody
        stage={stage}
        policy={policy}
        policyForm={policyForm}
        countriesForm={countriesForm}
        bookkeeperIds={bookkeeperIds}
        loadingPolicies={loadingPolicies}
        setBookkeeperIds={setBookkeeperIds}
        addEmployeesToPolicy={addEmployeesToPolicy}
        setAddEmployeesToPolicy={setAddEmployeesToPolicy}
      />
    </Modal>
  )
}

export default observer(PayrollAppModal)
